import { Document, Page, StyleSheet, Text, View } from "@react-pdf/renderer";
import type { ReactNode } from "react";
import type { PurchaseOrderPdfModel } from "./purchaseOrderPdfModel";

const BRAND = "#B03858";
const SLATE_800 = "#1e293b";
const SLATE_600 = "#475569";
const SLATE_500 = "#64748b";
const SLATE_200 = "#e2e8f0";
const SLATE_50 = "#f8fafc";

const styles = StyleSheet.create({
  page: {
    paddingTop: 40,
    paddingBottom: 48,
    paddingHorizontal: 44,
    fontFamily: "Helvetica",
    fontSize: 10,
    color: SLATE_800,
    lineHeight: 1.45,
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 24,
    paddingBottom: 16,
    borderBottomWidth: 2,
    borderBottomColor: BRAND,
  },
  logoBox: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  logoMark: {
    width: 28,
    height: 28,
    borderRadius: 6,
    backgroundColor: BRAND,
    alignItems: "center",
    justifyContent: "center",
  },
  logoMarkText: {
    color: "#fff",
    fontSize: 14,
    fontFamily: "Helvetica-Bold",
  },
  logoTitle: {
    fontSize: 14,
    fontFamily: "Helvetica-Bold",
    color: SLATE_800,
  },
  logoSubtitle: {
    fontSize: 8,
    color: SLATE_500,
  },
  docTitle: {
    fontSize: 11,
    fontFamily: "Helvetica-Bold",
    color: BRAND,
    textAlign: "right",
    maxWidth: 180,
  },
  section: {
    marginBottom: 16,
  },
  sectionTitle: {
    fontSize: 10,
    fontFamily: "Helvetica-Bold",
    color: BRAND,
    marginBottom: 8,
    textTransform: "uppercase",
    letterSpacing: 0.6,
  },
  fieldRow: {
    flexDirection: "row",
    marginBottom: 4,
  },
  fieldLabel: {
    width: 120,
    fontSize: 9,
    color: SLATE_500,
  },
  fieldValue: {
    flex: 1,
    fontSize: 9,
    color: SLATE_800,
  },
  observacaoBox: {
    backgroundColor: SLATE_50,
    borderWidth: 1,
    borderColor: SLATE_200,
    borderRadius: 4,
    padding: 10,
  },
  observacaoText: {
    fontSize: 9,
    color: SLATE_600,
  },
  tableHeader: {
    flexDirection: "row",
    backgroundColor: SLATE_50,
    borderBottomWidth: 1,
    borderBottomColor: SLATE_200,
    paddingVertical: 6,
    paddingHorizontal: 4,
  },
  tableRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: SLATE_200,
    paddingVertical: 5,
    paddingHorizontal: 4,
  },
  colNum: { width: "7%", fontSize: 8 },
  colCode: { width: "12%", fontSize: 8 },
  colDesc: { width: "33%", fontSize: 8 },
  colQty: { width: "12%", fontSize: 8, textAlign: "right" },
  colUnit: { width: "8%", fontSize: 8, textAlign: "center" },
  colPrice: { width: "14%", fontSize: 8, textAlign: "right" },
  colTotal: { width: "14%", fontSize: 8, textAlign: "right" },
  th: {
    fontFamily: "Helvetica-Bold",
    color: SLATE_500,
    fontSize: 7,
    textTransform: "uppercase",
  },
  totalRow: {
    flexDirection: "row",
    justifyContent: "flex-end",
    marginTop: 8,
    paddingTop: 8,
    borderTopWidth: 1,
    borderTopColor: SLATE_200,
  },
  totalLabel: {
    fontSize: 10,
    fontFamily: "Helvetica-Bold",
    color: SLATE_600,
    marginRight: 12,
  },
  totalValue: {
    fontSize: 10,
    fontFamily: "Helvetica-Bold",
    color: SLATE_800,
  },
  footer: {
    position: "absolute",
    bottom: 28,
    left: 44,
    right: 44,
    flexDirection: "row",
    justifyContent: "space-between",
    borderTopWidth: 1,
    borderTopColor: SLATE_200,
    paddingTop: 8,
  },
  footerText: {
    fontSize: 8,
    color: SLATE_500,
  },
  empty: {
    fontSize: 9,
    color: SLATE_500,
    fontStyle: "italic",
  },
});

function FieldRow({ label, value }: { label: string; value: string }) {
  return (
    <View style={styles.fieldRow}>
      <Text style={styles.fieldLabel}>{label}</Text>
      <Text style={styles.fieldValue}>{value || "—"}</Text>
    </View>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <View style={styles.section}>
      <Text style={styles.sectionTitle}>{title}</Text>
      {children}
    </View>
  );
}

interface Props {
  data: PurchaseOrderPdfModel;
}

export function PurchaseOrderPdfDocument({ data }: Props) {
  return (
    <Document title={`Pedido de Compra — ${data.orderNumber}`}>
      <Page size="A4" style={styles.page}>
        <View style={styles.header}>
          <View style={styles.logoBox}>
            <View style={styles.logoMark}>
              <Text style={styles.logoMarkText}>S</Text>
            </View>
            <View>
              <Text style={styles.logoTitle}>SEMOG</Text>
              <Text style={styles.logoSubtitle}>SEMOG BI — Gestão Integrada</Text>
            </View>
          </View>
          <Text style={styles.docTitle}>Pedido de Compra #{data.orderNumber}</Text>
        </View>

        <Section title="Dados do Pedido">
          <FieldRow label="Nº do pedido" value={data.orderNumber} />
          <FieldRow label="Fornecedor" value={data.supplier} />
          <FieldRow label="Data" value={data.date} />
          <FieldRow label="Status" value={data.status} />
          <FieldRow label="Centro de custo" value={data.costCenter} />
          <FieldRow label="Valor" value={data.value} />
          {data.billId ? <FieldRow label="Título (pagamento)" value={data.billId} /> : null}
        </Section>

        <Section title="Itens">
          {data.items.length === 0 ? (
            <Text style={styles.empty}>Nenhum item encontrado para este pedido.</Text>
          ) : (
            <>
              <View style={styles.tableHeader}>
                <Text style={[styles.colNum, styles.th]}>#</Text>
                <Text style={[styles.colCode, styles.th]}>Código</Text>
                <Text style={[styles.colDesc, styles.th]}>Descrição</Text>
                <Text style={[styles.colQty, styles.th]}>Qtd</Text>
                <Text style={[styles.colUnit, styles.th]}>Un</Text>
                <Text style={[styles.colPrice, styles.th]}>Unit.</Text>
                <Text style={[styles.colTotal, styles.th]}>Total</Text>
              </View>
              {data.items.map((it, idx) => (
                <View key={`${it.itemNumber}-${it.code}-${idx}`} style={styles.tableRow} wrap={false}>
                  <Text style={styles.colNum}>{it.itemNumber}</Text>
                  <Text style={styles.colCode}>{it.code}</Text>
                  <Text style={styles.colDesc}>{it.description}</Text>
                  <Text style={styles.colQty}>{it.quantity}</Text>
                  <Text style={styles.colUnit}>{it.unit}</Text>
                  <Text style={styles.colPrice}>{it.unitPrice}</Text>
                  <Text style={styles.colTotal}>{it.total}</Text>
                </View>
              ))}
              <View style={styles.totalRow}>
                <Text style={styles.totalLabel}>Total dos itens</Text>
                <Text style={styles.totalValue}>{data.itemsTotal}</Text>
              </View>
            </>
          )}
        </Section>

        <Section title="Observações">
          <View style={styles.observacaoBox}>
            <Text style={styles.observacaoText}>{data.observations}</Text>
          </View>
        </Section>

        <View style={styles.footer} fixed>
          <Text style={styles.footerText}>Documento emitido em: {data.emitidoEm}</Text>
          <Text style={styles.footerText}>Gerado por: {data.geradoPor}</Text>
        </View>
      </Page>
    </Document>
  );
}
